import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Check, Clock, Calendar, Wallet, GraduationCap,
  Award, Phone, ShieldCheck, Sparkles, BookOpen, Users, Mail, CalendarClock, MessageCircle, ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { COURSE_LIST, CATEGORY_LABELS, COURSE_SKRIPTE } from "@/data/courses";
import { CampusBadge } from "@/components/site/HeldenCampus";
import BookingDialog from "@/components/site/BookingDialog";
import InfopaketDialog from "@/components/site/InfopaketDialog";
import { TrustInline } from "@/components/site/TrustInline";
import { formatNextStart, nextMondayLabel } from "@/lib/nextStart";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { trackViewItem } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { ReadAloudButton } from "@/components/site/ReadAloudButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_FAQS = {
  "pdl": {
    title: "Häufige Fragen zur PDL-Weiterbildung",
    items: [
      { q: "Wie lange dauert die PDL-Weiterbildung?", a: "Der Kurs umfasst 720 Unterrichtseinheiten und dauert bei den meisten etwa 36 Wochen. Mit entsprechender Vorerfahrung oder bereits absolvierten Teilen ist er auch schneller möglich." },
      { q: "Was kostet die PDL-Weiterbildung?", a: "4.690 €, alternativ in 3, 6 oder 12 Raten ab 390,83 € pro Monat, ohne Aufschlag." },
      { q: "Gibt es einen Bildungsgutschein?", a: "Nein, die PDL-Weiterbildung wird direkt gebucht. Eine Übernahme der Kosten durch den Arbeitgeber ist in vielen Fällen möglich, wir beraten dich dazu gern." },
      { q: "Wer kann teilnehmen?", a: "Die Weiterbildung richtet sich an Pflegefachkräfte, die in die Leitung eines Pflegedienstes oder einer Einrichtung wechseln möchten." },
      { q: "Kann ich eine begonnene Weiterbildung bei euch fortsetzen?", a: "Ja. Bereits absolvierte Teile können angerechnet werden. Sprich uns an, wir klären gemeinsam, wo du stehst." },
      { q: "Wann geht es los?", a: "Der Einstieg ist jeden Montag möglich." },
      { q: "Wie läuft der Kurs ab?", a: "E-Learning Modul für Modul, ergänzt durch Live-Termine, die individuell mit der Dozentin oder dem Dozenten abgestimmt werden. Das offizielle Kursskript ist kostenlos dabei." },
    ],
  },
  "ambulanter-pflegeassistent": {
    title: "Häufige Fragen",
    items: [
      { q: "Wie lange dauert die Weiterbildung?", a: "300 Unterrichtseinheiten in etwa 9 Wochen, dazu ein 3-monatiges Praktikum, das du zeitlich frei legen kannst." },
      { q: "Was kostet sie?", a: "2.390 €, oder in 3, 6 oder 12 Raten ab 199,16 € pro Monat, ohne Aufschlag." },
      { q: "Gibt es eine Förderung?", a: "Ja, mit Bildungsgutschein, Arbeitgeber-Förderung oder Ratenzahlung." },
      { q: "Brauche ich Berufserfahrung?", a: "Nein, der Einstieg ist mit wenig oder keiner Berufserfahrung möglich." },
      { q: "Wie läuft der Kurs ab? Geht das neben dem Beruf?", a: "E-Learning Modul für Modul – du lernst, wann immer du Zeit hast, auch im Schichtdienst. Ergänzt durch Live-Termine, die individuell mit der Dozentin oder dem Dozenten abgestimmt werden. Das offizielle Kursskript ist kostenlos dabei." },
      { q: "Wie läuft das Praktikum?", a: "Zum Kurs gehört ein 3-monatiges Praktikum. Die zeitliche Gestaltung ist flexibel – du kannst es so legen, wie es zu deiner Situation passt, z. B. blockweise oder gestreckt." },
      { q: "Wann startet der nächste Kurs?", a: "Der Einstieg ist jeden Montag möglich." },
    ],
  },
  "pflegeassistent-lg1-lg2": {
    title: "Häufige Fragen",
    items: [
      { q: "Wie lange dauert die Weiterbildung?", a: "208 Unterrichtseinheiten in etwa 6,5 Wochen." },
      { q: "Was kostet sie?", a: "1.499 €, oder in 3, 6 oder 12 Raten ab 124,91 € pro Monat, ohne Aufschlag." },
      { q: "Gibt es eine Förderung?", a: "Bildungsgutschein möglich, ebenso Arbeitgeber-Förderung oder Ratenzahlung." },
      { q: "Welche Voraussetzungen gibt es?", a: "Pflege-Grundqualifikation und Berufserfahrung als Pflegehilfskraft. Danach ist Behandlungspflege LG1 und LG2 abrechenbar." },
      { q: "Wann startet der nächste Kurs?", a: "Der Einstieg ist jeden Montag möglich." },
    ],
  },
  "wundexperte": {
    title: "Häufige Fragen",
    items: [
      { q: "Wie lange dauert die Weiterbildung?", a: "84 Unterrichtseinheiten in etwa 5 Wochen, E-Learning Modul für Modul, ergänzt durch Live-Termine, die individuell mit der Dozentin oder dem Dozenten abgestimmt werden." },
      { q: "Was kostet sie?", a: "1.690 €, oder in 3, 6 oder 12 Raten ab 140,83 € pro Monat, ohne Aufschlag." },
      { q: "Wer kann teilnehmen?", a: "3-jährig examinierte Pflegefachkräfte, MFA, OTA, Podologinnen und Podologen (ICW-Zulassung)." },
      { q: "Gibt es eine Förderung?", a: "Nein, einen Bildungsgutschein gibt es für den Wundexperten nicht. Du buchst direkt, oder in 3, 6 oder 12 Raten ab 140,83 € pro Monat, ohne Aufschlag." },
      { q: "Wann startet der nächste Kurs?", a: "Der Einstieg ist jeden Montag möglich." },
    ],
  },
};

function injectFaqJsonLd(course) {
  const existing = document.getElementById("ld-faq");
  const faq = COURSE_FAQS[course.id];
  if (!faq) {
    if (existing) existing.remove();
    return;
  }
  const json = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.items.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  let tag = existing;
  if (!tag) {
    tag = document.createElement("script");
    tag.id = "ld-faq";
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(json);
}

function getCourseBySlug(slug) {
  return COURSE_LIST.find((c) => c.id === slug);
}

function injectJsonLd(course, url) {
  // Schema.org/Course – improves Google Ads quality score + rich snippets
  const json = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "provider": {
      "@type": "Organization",
      "name": "Dickies-Campus",
      "sameAs": "https://dickies-helden.de",
    },
    "url": url,
    "educationalCredentialAwarded": course.outcomes?.[0] || course.subtitle,
    "timeRequired": course.duration,
    "offers": {
      "@type": "Offer",
      "category": course.funding,
      "price": String(course.price || "").replace(/[^\d,]/g, "").replace(",", "."),
      "priceCurrency": "EUR",
    },
  };
  let tag = document.getElementById("ld-course");
  if (!tag) {
    tag = document.createElement("script");
    tag.id = "ld-course";
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(json);
}

export default function CourseLanding() {
  const { slug } = useParams();
  const course = getCourseBySlug(slug);
  const [submitting, setSubmitting] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [feedback, setFeedback] = useState(null);
  const [infopaketOpen, setInfopaketOpen] = useState(false);
  const [exitIntent, setExitIntent] = useState(false);

  // Exit-Intent (Desktop, 1× pro Session): Maus verlässt Viewport nach oben
  useEffect(() => {
    if (!course) return undefined;
    const onLeave = (e) => {
      if (e.clientY > 10) return;
      try {
        if (sessionStorage.getItem("dh-exit-infopaket") === "1") return;
        sessionStorage.setItem("dh-exit-infopaket", "1");
      } catch { /* ignore */ }
      setExitIntent(true);
      setInfopaketOpen(true);
    };
    const timer = setTimeout(() => document.addEventListener("mouseleave", onLeave), 8000);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", onLeave); };
  }, [course]);

  useEffect(() => {
    if (!course) return;
    const fullTitle = `${course.title} · ${course.subtitle} | Dickies-Campus`;
    document.title = fullTitle;

    const desc = `${course.description.substring(0, 155)}…`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = desc;
    injectJsonLd(course, window.location.href);
    injectFaqJsonLd(course);
    trackViewItem(course.id, course.title, Number(String(course.price).replace(/[^\d]/g, "")) || 0);
  }, [course]);

  if (!course) return <Navigate to="/" replace />;

  const handleCheckout = () => setBookingOpen(true);

  const handleLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || "noch nicht angegeben",
        audience: course.category === "pflicht" ? "pflegedienst" : "pflegekraft",
        course_interest: course.title,
        message: form.message,
        consent: true,
        source: `course-lp:${course.id}`,
        attribution: getAttribution(),
      });
      setFeedback({ type: "success", text: "Danke! Wir melden uns innerhalb von 24 Stunden bei dir." });
      metaLead(`kursseite:${course.id}`);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      setFeedback({ type: "error", text: "Anfrage konnte nicht gesendet werden – bitte direkt anrufen: 0211 29273330." });
    }
    setSubmitting(false);
  };

  const schedules = Array.isArray(course.schedule) ? course.schedule : [course.schedule];
  const skript = COURSE_SKRIPTE[course.id];
  const priceNum = (() => {
    const s = String(course.price).replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", ".");
    return Number(s) || 0;
  })();
  const monthlyLabel = priceNum >= 300
    ? (Math.floor((priceNum * 100) / 12) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />

      <main className="pb-24 lg:pb-32">
        {/* === DESENIO HERO === */}
        <section
          className="relative overflow-hidden"
          style={{
            backgroundColor: {
              einstieg: "#F9E7E3",
              erfahren: "#F6E8DC",
              fuehrung: "#EFEDE3",
              pflicht: "#E9EDE5",
            }[course.category] || "#F6E8DC",
          }}
        >
          <div aria-hidden="true" className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-14 lg:pt-44 lg:pb-20">
            <Link to="/#kurse" data-testid="course-back-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold text-sand-500 hover:text-terra transition-colors mb-8">
              <ArrowLeft className="h-3.5 w-3.5" /> Zur Kursübersicht
            </Link>
            <div className="flex items-center gap-3.5 flex-wrap">
              <span className="font-poster text-[11px] uppercase tracking-[0.3em] font-bold text-terra">
                {CATEGORY_LABELS[course.category]}
              </span>
              <span className="h-px w-14 bg-terra/50" />
              {course.funding && (
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold rounded-full border border-terra/50 text-terra px-3 py-1">
                  {course.funding}
                </span>
              )}
            </div>
            <h1 className="font-magazine text-sand-800 font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.06] mt-5 max-w-4xl">
              {course.title}
            </h1>
            <p className="mt-4 font-magazine italic text-lg sm:text-2xl text-terra leading-snug max-w-2xl">
              {course.subtitle}
            </p>
            <div className="mt-5">
              <ReadAloudButton
                text={[
                  course.title, course.subtitle, course.description,
                  course.outcomes ? "Das bringt dir der Kurs: " + course.outcomes.join(". ") : "",
                  `Dauer: ${course.duration || ""}. Preis: ${course.price || ""}.`,
                  "Alle Infos und die kostenlose Beratung findest du auf dieser Seite.",
                ].filter(Boolean).join(" ")}
              />
            </div>
          </div>
        </section>

        {/* === CONTENT === */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <p className="text-sand-700 text-[15px] sm:text-base leading-[1.7] max-w-2xl">
              {course.description}
            </p>

            {/* Outcomes – the "Was bringt's mir" promise above the fold */}
            {course.outcomes && (
              <ul className="mt-8 space-y-3 max-w-2xl">
                {course.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-[15px] text-sand-800">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0">
                      <Check className="h-3 w-3" strokeWidth={2.8} />
                    </span>
                    <span className="font-medium">{o}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild data-testid="course-lp-primary-cta" className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-7 text-base font-semibold">
                <a href="#anfrage">Kostenlose Beratung
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
                </a>
              </Button>
              <Button onClick={handleCheckout} data-testid="course-lp-secondary-cta"
                variant="outline"
                className="rounded-full h-14 px-7 text-base font-medium border border-sand-800 text-sand-800 hover:bg-white bg-transparent">
                Direkt buchen ({course.price})
              </Button>
            </div>

            {monthlyLabel && (
              <p data-testid="course-lp-raten-hint" className="mt-4 inline-flex items-center gap-2 rounded-full bg-sage/15 border border-sage/40 text-sand-800 text-[13px] font-medium px-4 py-2">
                <CalendarClock className="h-4 w-4 text-sage-deep shrink-0" strokeWidth={1.8} />
                <span>Ratenzahlung möglich: <strong className="text-sage-deep">ab {monthlyLabel} €/Monat</strong> · 3, 6 oder 12 Raten · ohne Aufschlag</span>
              </p>
            )}

            {/* Urgency: konkreter nächster Starttermin */}
            <p data-testid="course-lp-next-start" className="mt-4 flex items-center gap-2 text-[13.5px] font-semibold text-sand-800">
              <Calendar className="h-4 w-4 text-terra shrink-0" strokeWidth={1.8} />
              Nächster Start: <span className="text-terra">{formatNextStart(course.nextStart)}</span>
            </p>

            {/* Social Proof direkt am CTA */}
            <div className="mt-5">
              <TrustInline testId="course-lp-trust" />
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              <TrackedPhoneLink source={`course-${course.id || "landing"}`} className="inline-flex items-center gap-2 text-sm text-sand-500 hover:text-sand-800">
                <Phone className="h-4 w-4" strokeWidth={1.5} />
                oder direkt anrufen: <span className="font-semibold text-sand-800">0211 29273330</span>
              </TrackedPhoneLink>
              <a
                href={`https://wa.me/491622572545?text=${encodeURIComponent(`Hallo! Ich interessiere mich für "${course.title}".`)}`}
                target="_blank" rel="noreferrer"
                data-testid="course-lp-whatsapp"
                className="inline-flex items-center gap-2 text-sm text-sand-500 hover:text-terra transition-colors"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                <span className="font-semibold text-sand-800">WhatsApp</span>
              </a>
            </div>

            {/* 3 Schritte zur Anmeldung · klarer Conversion-Pfad */}
            <div data-testid="course-lp-steps" className="mt-10 rounded-3xl border border-cream-300 bg-white p-6 md:p-7 max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra mb-5">
                So einfach geht&rsquo;s — in 3 Schritten
              </div>
              <ol className="space-y-4">
                {[
                  { n: "1", t: "Kostenlose Beratung", d: "Anruf oder Formular – wir klären in 10 Minuten, ob der Kurs zu dir passt.", href: "#anfrage", testid: "course-lp-step-beratung" },
                  { n: "2", t: "Fördercheck", d: /bildungsgutschein/i.test(course.funding || "") ? "Wir prüfen gemeinsam Bildungsgutschein, Arbeitgeber-Förderung oder Ratenzahlung." : "Wir prüfen gemeinsam Arbeitgeber-Förderung oder Ratenzahlung (3, 6 oder 12 Raten, ohne Aufschlag).", href: "#anfrage", testid: "course-lp-step-foerderung" },
                  { n: "3", t: "Anmeldung & Start", d: "Online anmelden – Kursstart jeden Montag, alle Unterlagen inklusive.", onClick: true, testid: "course-lp-step-anmeldung" },
                ].map((s) => (
                  <li key={s.n} className="flex items-start gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-terra text-white text-[13px] font-bold">
                      {s.n}
                    </span>
                    <div className="flex-1">
                      {s.onClick ? (
                        <button type="button" onClick={handleCheckout} data-testid={s.testid}
                          className="text-left group/step">
                          <span className="font-semibold text-[15px] text-sand-800 border-b border-terra/40 group-hover/step:text-terra transition-colors">{s.t}</span>
                          <span className="block text-[13.5px] text-sand-600 leading-snug mt-0.5">{s.d}</span>
                        </button>
                      ) : (
                        <a href={s.href} data-testid={s.testid} className="group/step block">
                          <span className="font-semibold text-[15px] text-sand-800 border-b border-terra/40 group-hover/step:text-terra transition-colors">{s.t}</span>
                          <span className="block text-[13.5px] text-sand-600 leading-snug mt-0.5">{s.d}</span>
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right: Fact-sheet card */}
          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-7 md:p-8 sticky top-32">
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-brand-red mb-5">
                Kurs-Steckbrief
              </div>
              <ul className="space-y-4 text-[14px]">
                <FactRow icon={Clock} label="Dauer" value={`${course.duration}${course.weeks ? ` · ${course.weeks}` : ""}`} />
                <FactRow icon={Calendar} label="Nächster Start" value={formatNextStart(course.nextStart)} />
                <FactRow icon={GraduationCap} label="Zielgruppe" value={course.audience} />
                <FactRow icon={Wallet} label="Preis" value={monthlyLabel ? `${course.price} — oder ab ${monthlyLabel} €/Monat in Raten` : course.price} />
                {course.priceNote && <FactRow icon={ShieldCheck} label="Preisdetails" value={course.priceNote} />}
                <FactRow icon={ShieldCheck} label="Förderung" value={course.funding} />
                {course.format && <FactRow icon={BookOpen} label="Format" value={course.format} />}
                {skript && (
                  <FactRow icon={BookOpen} label="Inklusive" value={`Offizielles Kursskript (${skript.pages.toLocaleString("de-DE")} Seiten) – kostenlos`} />
                )}
              </ul>
              <div className="mt-6 pt-5 border-t border-cream-300">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2">Termine</div>
                <ul className="space-y-1.5">
                  {schedules.filter(Boolean).map((s) => (
                    <li key={s} className="text-[13px] text-sand-700 leading-snug">· {s}</li>
                  ))}
                </ul>
              </div>

              {/* Infopaket per E-Mail – Lead-Capture statt freiem Download */}
              <div className="mt-6 pt-5 border-t border-cream-300">
                <Button
                  onClick={() => { setExitIntent(false); setInfopaketOpen(true); }}
                  data-testid="course-lp-flyer-btn"
                  className="w-full rounded-full bg-sand-800 hover:bg-sand-900 text-white h-12 px-5 text-[14px] font-semibold inline-flex items-center justify-center gap-2"
                >
                  <Mail className="h-4 w-4" strokeWidth={2} />
                  Infopaket per E-Mail erhalten
                </Button>
                <p className="mt-2 text-[12px] text-sand-500 text-center leading-snug">
                  Flyer + alle Kursdetails + Förder-Infos – kostenlos in dein Postfach.
                </p>
              </div>
            </div>

            {/* Campus-Badge below fact-sheet */}
            <div className="mt-5">
              <CampusBadge />
            </div>
          </aside>
        </section>

        {/* === Curriculum === */}
        {course.curriculum && (
          <section className="bg-peach-50 mt-24 py-20 lg:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">Was du lernst</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] text-sand-800 font-normal tracking-tight leading-[1.12] max-w-3xl">
                Curriculum – kompakt &amp; praxisnah.
              </h2>

              <div className="mt-12 grid md:grid-cols-2 gap-4 lg:gap-5">
                {course.curriculum.map((m, i) => (
                  <div key={m} className="flex items-start gap-4 rounded-2xl bg-white p-5 border border-cream-300 shadow-soft">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-terra/12 text-terra font-display text-[15px] font-semibold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[14px] text-sand-800 leading-snug font-medium">{m}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* === PDL: Wiedereinstieg nach Abbruch === */}
        {course.id === "pdl" && (
          <section data-testid="pdl-resume-section" className="mt-24 lg:mt-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-8 md:p-12">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">Wiedereinstieg</span>
                </div>
                <h2 className="font-display normal-case text-3xl sm:text-4xl text-sand-800 font-normal tracking-tight leading-[1.12] max-w-3xl">
                  Weiterbildung unterbrochen? So machst du weiter
                </h2>
                <p className="mt-5 text-sand-600 text-[15.5px] leading-[1.75] max-w-3xl">
                  Du hast die PDL-Weiterbildung schon begonnen und musstest sie abbrechen, zum Beispiel wegen
                  eines Jobwechsels, der Insolvenz deines Arbeitgebers oder aus privaten Gründen? Sprich mit uns.
                  Wir schauen gemeinsam, wo du stehst, rechnen bereits absolvierte Teile an und bringen dich zum
                  Abschluss. Je nach Vorerfahrung geht es schneller als die üblichen 36 Wochen.
                </p>
                <div className="mt-8">
                  <Button asChild data-testid="pdl-resume-cta" className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-7 text-base font-semibold">
                    <a href="#anfrage">Kostenlose Beratung
                      <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* === Skript inklusive === */}
        {skript && (
          <section data-testid="course-skript-section" className="mt-24 lg:mt-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="relative rounded-3xl bg-coral overflow-hidden">
                {/* dezente Grain-/Tiefe-Ebene */}
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 85% 10%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 55%), radial-gradient(90% 80% at 10% 100%, rgba(82,42,37,0.16) 0%, rgba(82,42,37,0) 60%)" }} />
                <div className="relative grid md:grid-cols-12 items-center">
                  {/* Cover */}
                  <div className="md:col-span-4 lg:col-span-3 flex justify-center py-12 md:py-14 px-8">
                    <img
                      src={skript.cover}
                      alt={skript.title}
                      data-testid="course-skript-cover"
                      className="w-44 sm:w-52 rounded-lg shadow-2xl rotate-[-3deg] hover:rotate-0 transition-transform duration-500 ring-1 ring-white/25"
                      loading="lazy"
                    />
                  </div>
                  {/* Text */}
                  <div className="md:col-span-8 lg:col-span-9 px-8 md:px-4 lg:px-8 pb-12 md:py-14 md:pr-12">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-cream-100">
                        Inklusive · Ohne Aufpreis
                      </span>
                      {/* Farbpalette-Punkte */}
                      <span aria-hidden="true" className="inline-flex items-center gap-1.5 ml-1">
                        {["#E08B6D", "#B97F76", "#6E866F", "#7D7F72", "#CFC9BC", "#E8A0AC"].map((c) => (
                          <span key={c} className="h-2.5 w-2.5 rounded-full ring-1 ring-white/30" style={{ backgroundColor: c }} />
                        ))}
                      </span>
                    </div>
                    <h2 className="font-magazine text-2xl sm:text-3xl lg:text-[2.4rem] text-cream-100 font-normal tracking-tight leading-[1.14]">
                      Das offizielle Kursskript – <span className="italic underline decoration-cream-100/60 decoration-2 underline-offset-4">geschenkt</span> zur Weiterbildung.
                    </h2>
                    <p className="mt-4 text-cream-100/90 text-[15px] leading-[1.7] max-w-2xl">
                      Jede:r Teilnehmer:in erhält {skript.volumes ? "unsere kompletten Academy-Skripte" : "unser komplettes Academy-Skript"}{" "}
                      <strong className="text-white">„{skript.title}“</strong> kostenlos dazu – zum Nachschlagen
                      während des Kurses und als Nachschlagewerk für den Berufsalltag danach.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2.5">
                      <span data-testid="course-skript-pages" className="inline-flex items-center gap-2 rounded-full bg-cream-100 text-coral-deep text-[13px] font-bold px-4 py-2 shadow-soft">
                        <BookOpen className="h-3.5 w-3.5" strokeWidth={2.2} />
                        {skript.volumes || `${skript.pages.toLocaleString("de-DE")} Seiten`}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-rosewood/35 border border-cream-100/40 text-cream-100 text-[13px] font-medium px-4 py-2">
                        PDF · digital + druckbar
                      </span>
                      <span className="inline-flex items-center rounded-full bg-sage-deep text-[#FBF6F0] text-[13px] font-semibold px-4 py-2 shadow-soft" style={{ color: "#FBF6F0" }}>
                        Im Shop nur im Lernpaket ({skript.paketWert} €) – für dich 0 €
                      </span>
                      {skript.amazonUrl && (
                        <a
                          href={skript.amazonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="course-skript-amazon-link"
                          className="inline-flex items-center gap-2 rounded-full bg-[#FF9900] text-[#1A1208] text-[13px] font-bold px-4 py-2 shadow-soft hover:brightness-105 transition"
                        >
                          <ShoppingCart className="h-3.5 w-3.5" strokeWidth={2.2} />
                          Als gedrucktes Buch bei Amazon ({skript.amazonPreis} €)
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* === Trust strip === */}
        <section className="mt-20 lg:mt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {[
                { icon: Award, t: "AZAV-zertifiziert", d: "Zugelassener Bildungsträger" },
                { icon: Users, t: "Über 1.000 Pflegekräfte", d: "Erfolgreich qualifiziert" },
                { icon: Calendar, t: "Start jeden Montag", d: "Flexible Einstiege" },
                course.funding.includes("Bildungsgutschein")
                  ? { icon: Sparkles, t: "Bildungsgutschein", d: "Meist 100 % gefördert" }
                  : { icon: Sparkles, t: "Sofort buchbar", d: "Auch Ratenkauf via Klarna" },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-3.5">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred">
                    <b.icon className="h-4.5 w-4.5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <div className="font-display text-sand-800 text-[17px] leading-tight">{b.t}</div>
                    <div className="text-[12px] text-sand-500 mt-1">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* === Services-Hinweis (nur PDL: Gründung/Backoffice) === */}
        {course.id === "pdl" && (
          <section data-testid="pdl-services-hint" className="mt-24 lg:mt-28">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white/85 border border-sand-800/10 border-l-[3px] border-l-terra rounded-2xl p-7 md:p-8 shadow-[0_24px_50px_-36px_rgba(82,42,37,0.35)]">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-2">Nach der Weiterbildung</div>
                <h3 className="font-magazine text-2xl text-sand-800">Du gründest einen eigenen Pflegedienst?</h3>
                <p className="mt-3 text-sand-600 text-[15px] leading-relaxed">
                  Homepage, Patientengewinnung über Google Ads, CRM, Abrechnung (SGB V/XI) und QM-Aufbau für die
                  Zulassung — das übernehmen wir mit unserer Technik-Partnerin Joudi Ltd. für dich.
                </p>
                <Link to="/services" data-testid="pdl-services-link" className="mt-5 inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold text-sm px-5 py-2.5 transition">
                  Services für Pflegedienste ansehen
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* === Kurs-FAQ (sichtbar, pro Kurs in COURSE_FAQS gepflegt) === */}
        {COURSE_FAQS[course.id] && (
          <section data-testid={`${course.id}-faq-section`} className="mt-24 lg:mt-28">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">FAQ</span>
              </div>
              <h2 className="font-display normal-case text-3xl sm:text-4xl text-sand-800 font-normal tracking-tight leading-[1.12]">
                {COURSE_FAQS[course.id].title}
              </h2>
              <Accordion type="single" collapsible className="mt-10">
                {COURSE_FAQS[course.id].items.map((f, i) => (
                  <AccordionItem key={f.q} value={`course-faq-${i}`} className="border-b border-cream-300">
                    <AccordionTrigger data-testid={`${course.id}-faq-trigger-${i}`} className="text-left py-6 hover:no-underline group">
                      <span className="flex items-baseline gap-4">
                        <span className="text-[11px] font-semibold text-terra tracking-wider shrink-0">Nº {String(i + 1).padStart(2, "0")}</span>
                        <span className="font-magazine text-[1.05rem] md:text-[1.2rem] text-sand-800 leading-tight group-data-[state=open]:text-terra transition-colors">{f.q}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-7 pl-12 pr-4">
                      <p className="text-sand-600 text-[15px] leading-[1.75]">{f.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* === Lead form === */}
        <section id="anfrage" className="mt-24 lg:mt-32">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-8 md:p-12">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">Beratung anfordern</span>
              </div>
              <h2 className="font-display text-2xl sm:text-4xl text-sand-800 font-normal tracking-tight leading-[1.12] break-words [hyphens:auto]">
                Persönliche Beratung zum Kurs <span className="text-terra">&bdquo;{course.title}&ldquo;</span>
              </h2>
              <p className="mt-4 text-sand-600 text-[15px] leading-relaxed">
                Wir rufen dich innerhalb von 24 Stunden zurück – kostenlos &amp; unverbindlich.
              </p>

              <form onSubmit={handleLead} className="mt-8 grid gap-4">
                <input
                  required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dein Name *"
                  data-testid="course-lp-form-name"
                  className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    required type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="E-Mail *"
                    data-testid="course-lp-form-email"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  />
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Telefon"
                    data-testid="course-lp-form-phone"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  />
                </div>
                <textarea
                  rows={4} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Nachricht (optional)"
                  data-testid="course-lp-form-message"
                  className="rounded-2xl border border-cream-300 bg-cream-50 px-5 py-3 text-[15px] outline-none focus:border-terra"
                />

                <Button type="submit" disabled={submitting} data-testid="course-lp-form-submit"
                  className="rounded-full bg-terra hover:bg-terra-dark text-white h-14 px-7 text-base font-semibold mt-2">
                  {submitting ? "Senden …" : "Beratung anfragen"}
                </Button>

                {feedback && (
                  <p className={`text-sm font-medium ${feedback.type === "success" ? "text-sage-dark" : "text-terra"}`}>
                    {feedback.text}
                  </p>
                )}

                <p className="text-[11px] text-sand-500 mt-1">
                  Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß
                  {" "}<Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>

      <BookingDialog course={course} open={bookingOpen} onOpenChange={setBookingOpen} />
      <InfopaketDialog course={course} open={infopaketOpen} onOpenChange={setInfopaketOpen} exitIntent={exitIntent} />
      <Footer />
    </div>
  );
}

function FactRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <li className="flex items-start gap-3.5">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream-100 text-terra">
        <Icon className="h-4 w-4" strokeWidth={1.6} />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sand-500">{label}</div>
        <div className="text-sand-800 font-medium leading-snug">{value}</div>
      </div>
    </li>
  );
}
